import { useMemo } from "react"

import { CheckOutlined, EyeOutlined } from "@ant-design/icons"
import { App, Button, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../../constants/user-routes"
import { SECTION_KEYS, SECTION_LABELS, SECTION_STEPS } from "../../constants/steps"
import { saveDraft } from "../constants/externalOps"
import { setWizard } from "../redux/configure.slice"
import { wizardView } from "../utils/hydrate"
import { allSectionCounts } from "../utils/sectionProgress"
import { isNextEnabled } from "../utils/sectionValidation"

import styles from "../shell.module.less"

const { Text } = Typography

const SectionItem = ({ stepNumber, label, count, active, complete, disabled, onClick, t }) => (
  <button
    type="button"
    className={cls(styles.sectionItem, {
      [styles.active]: active,
      [styles.isComplete]: complete,
      [styles.isDisabled]: disabled
    })}
    disabled={disabled}
    onClick={onClick}
    data-testid={`deflect-configure-step-${stepNumber}`}
  >
    <span className={styles.stepBadge}>{complete ? <CheckOutlined /> : stepNumber}</span>
    <span className={styles.sectionItemMeta}>
      {/*<span className={styles.sectionItemStep}>{t("Step {{n}}", { n: stepNumber })}</span>*/}
      <span className={styles.sectionItemStep}>{`Step ${stepNumber}`}</span>
      <span className={styles.sectionItemLabel}>{t(label)}</span>
    </span>
    <span className={styles.sectionItemCount}>
      {complete ? (
        <Text type="secondary" style={{ fontSize: 14, fontWeight: 500 }}>
          {t("Done")}
        </Text>
      ) : count.total > 0 ? (
        <Text type="secondary" style={{ fontSize: 14, fontWeight: 500 }}>
          {count.answered}/{count.total}
        </Text>
      ) : null}
    </span>
  </button>
)

// Mobile-only compact stepper. Five clickable badges in a row (number or check),
// with a caption row underneath: STEP N on the left, counter + Preview trigger
// on the right (per Maksym's mobile spec — the step title is dropped because
// the SectionForm heading immediately below already names the step). Same
// reachability rules as the desktop list: completed steps always clickable;
// unreached (past the frontier) are disabled.
const MobileStepper = ({
  currentSection,
  frontierIdx,
  isStepComplete,
  isStepDisplayComplete,
  perSectionCount,
  onNavigate,
  onOpenPreview,
  showPreviewToggle,
  t
}) => {
  const count = perSectionCount[currentSection] || { answered: 0, total: 0 }
  return (
    <div className={styles.mobileStepper}>
      <div className={styles.mobileStepperBadges}>
        {SECTION_KEYS.map((k, i) => {
          const complete = isStepDisplayComplete(k)
          const active = k === currentSection
          const disabled = i > frontierIdx && !isStepComplete(k)
          return (
            <button
              key={k}
              type="button"
              className={cls(styles.mobileStepBadge, styles.stepBadge, {
                [styles.active]: active,
                [styles.isComplete]: complete,
                [styles.isDisabled]: disabled
              })}
              disabled={disabled}
              onClick={() => onNavigate(k)}
              aria-label={t("Step {{n}}: {{label}}", {
                n: SECTION_STEPS[k].stepNumber,
                label: t(SECTION_LABELS[k])
              })}
            >
              {complete ? <CheckOutlined /> : SECTION_STEPS[k].stepNumber}
            </button>
          )
        })}
      </div>
      <div className={styles.mobileStepperMeta}>
        <span className={styles.mobileStepperStep}>
          {t("Step {{n}}", { n: SECTION_STEPS[currentSection]?.stepNumber })}
        </span>
        <span className={styles.mobileStepperRight}>
          {count.total > 0 ? (
            <Text type="secondary" className={styles.mobileStepperCount}>
              {isStepDisplayComplete(currentSection)
                ? t("Done")
                : `${count.answered}/${count.total}`}
            </Text>
          ) : null}
          {showPreviewToggle ? (
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={onOpenPreview}
              className={styles.previewToggle}
            >
              {t("Preview")}
            </Button>
          ) : null}
        </span>
      </div>
    </div>
  )
}

const LeftRail = ({
  currentSection,
  buildingBlocks,
  wizard,
  isHydrated,
  setWizard,
  showPreviewToggle,
  onOpenPreview,
  onRequestNext,
  usersViewForAdmin
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { search } = useLocation()
  const { notification } = App.useApp()
  const isMobile = useSelector((s) => !!s.nav?.isMobile)
  const perSectionCount = useMemo(() => allSectionCounts(buildingBlocks), [buildingBlocks])

  // Reachability comes from the server's completed_steps ("has this step ever
  // been committed via Next?"); `initialStep` is the last-incomplete pointer.
  const { isStepComplete, initialStep } = wizardView(wizard)

  // Badge/counter display uses a real-time overlay on top of the server flag:
  // a step that was Next'd once but is now locally invalid (merchant went back
  // and cleared a field) shows the answered/total counter, not "Done" — the
  // server's completed_steps only updates on save-draft response, so without
  // this the rail would still say Done while Next is blocked.
  const isStepDisplayComplete = (k) =>
    isStepComplete(k) && isNextEnabled(k, buildingBlocks?.[k] || {}, buildingBlocks)

  // The furthest step the merchant has ever reached: the server's
  // last-incomplete pointer, or the current section if they've navigated
  // backwards from it. Everything at or before this frontier stays reachable
  // via the rail (so bouncing back to Step 1 doesn't lock out Step 3 they've
  // already opened); anything beyond stays locked until Next takes them there.
  //
  // Support is not walking a wizard — they are browsing a finished profile, and
  // BLOCK 2.2 requires all 5 steps to be navigable however far the merchant got.
  // Pushing the frontier to the last step reaches every one of them without
  // duplicating the disabled/reachability rules below.
  const currentIdx = SECTION_KEYS.indexOf(currentSection)
  const frontierIdx = usersViewForAdmin
    ? SECTION_KEYS.length - 1
    : Math.max(currentIdx, SECTION_KEYS.indexOf(initialStep))

  // Jump to a section from either the desktop rail item or the mobile stepper
  // badge. Saves the section we're leaving first so half-typed values on the
  // current step aren't lost. Never before hydration: that would write whatever
  // was in the store back to the server as though the merchant had entered it.
  //
  // Block navigation when the current step was previously committed via Next
  // (`wizard.completedSteps`) but is now invalid — otherwise saveDraft would
  // clobber `completed_steps` on the server, moving the frontier backwards and
  // locking the merchant out of later steps (CHA1-2123). Fresh, never-committed
  // steps stay freely navigable per §11.2.
  // Support browses the same wizard under Business settings, where the step
  // lives at the admin path and the BA rides along in `?business_account_id=`.
  const goToSection = (k) =>
    usersViewForAdmin
      ? navigate({ pathname: RoutesPaths.receipts.admin_configure_profile_hash(k), search })
      : navigate(RoutesPaths.receipts.configure_profile_hash(k))

  const handleStepClick = (k) => {
    if (!isHydrated || k === currentSection) {
      goToSection(k)
      return
    }
    // Support only reads. saveDraft below would be a write, and the server
    // resolves that write against the ADMIN's own session BA (save-draft never
    // reads `business_account_id`), so letting it run would save the merchant's
    // answers onto support's account. The validity gate goes with it — there is
    // nothing to commit, so nothing to block.
    if (usersViewForAdmin) {
      goToSection(k)
      return
    }
    const currentWasCommitted = wizard?.completedSteps?.includes(currentSection)
    const currentValid = isNextEnabled(
      currentSection,
      buildingBlocks?.[currentSection] || {},
      buildingBlocks
    )
    if (currentWasCommitted && !currentValid) {
      onRequestNext?.(false)
      notification.warning({
        message: t("Some fields need attention"),
        description: t("Please fix the highlighted fields before moving on.")
      })
      return
    }
    saveDraft(buildingBlocks).then((result) => {
      if (result.ok) setWizard({ wizard: result.state.wizard })
    })
    goToSection(k)
  }

  return (
    <aside className={styles.rail}>
      <div className={styles.railTop}>
        {/* Prototype has no "Setup steps" header — the tab bar above and the
            step badges below already give enough context, so the rail opens
            straight into the step list. Preview trigger moves down into the
            MobileStepper meta row (mobile only). */}
        <div className={styles.sectionGroup}>
          {/* Always render the step list — post-submit editability is driven by
              the server (see index.js overlayStatus / formDisabled), not by a
              local "collapse to summary" toggle (CHA1-2083). */}
          <div className={styles.stepsList}>
            {isMobile ? (
              <MobileStepper
                currentSection={currentSection}
                frontierIdx={frontierIdx}
                isStepComplete={isStepComplete}
                isStepDisplayComplete={isStepDisplayComplete}
                perSectionCount={perSectionCount}
                onNavigate={handleStepClick}
                onOpenPreview={onOpenPreview}
                showPreviewToggle={showPreviewToggle}
                t={t}
              />
            ) : (
              SECTION_KEYS.map((k, i) => {
                const count = perSectionCount[k] || { answered: 0, total: 0 }
                // Anything at or before the frontier is reachable (already
                // opened at least once); anything beyond stays locked until
                // Next advances the frontier. Completed steps stay reachable
                // regardless — matters for the re-review flow.
                const disabled = i > frontierIdx && !isStepComplete(k)
                return (
                  <SectionItem
                    key={k}
                    stepNumber={SECTION_STEPS[k].stepNumber}
                    label={SECTION_LABELS[k]}
                    count={count}
                    active={k === currentSection}
                    complete={isStepDisplayComplete(k)}
                    disabled={disabled}
                    onClick={() => handleStepClick(k)}
                    t={t}
                  />
                )
              })
            )}
          </div>
        </div>
      </div>
    </aside>
  )
}

const mapStateToProps = (state) => ({
  buildingBlocks: state.receiptConfigure.profile.buildingBlocks,
  wizard: state.receiptConfigure.profile.wizard,
  isHydrated: !!state.receiptConfigure.profile.hydratedBaId
})

const mapDispatchToProps = (dispatch) => ({
  setWizard: (payload) => dispatch(setWizard(payload))
})

export default connect(mapStateToProps, mapDispatchToProps)(LeftRail)
